import { useEffect, useRef } from "react";
import { useTheme } from "next-themes";
import type { MotionValue } from "motion/react";
import { LOGO_POINTS, LOGO_WIRE_PAIRS } from "./cohartsMarkPoints";

type Variant = "iviz-core" | "foresight-sun" | "ai-brain" | "coharts-mark";

function hexToRgb01(hex: string) {
  const n = parseInt(hex.replace("#", ""), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255] as const;
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

// iViz core — chaotic outer data points converging into a pulsing analytical
// core, adapted from point_code.jsx's "Dopa Architecture" concept. Returns a
// 4th value (progress, 0=outer edge, 1=core) so the caller can recolor the
// particle from navy (edge) to accent-blue (core) each frame.
const IVIZ_CORE_SPEED = 0.4;
const IVIZ_CORE_CHAOS = 0.2;
const IVIZ_CORE_R = 0.14;
const IVIZ_CORE_OUTER_R = 1.85;
const GOLDEN_RATIO = 1.6180339887;

function ivizCorePosition(i: number, count: number, time: number) {
  const norm = i / count;
  const progress = (norm + time * IVIZ_CORE_SPEED * 0.2) % 1;
  const ease = Math.pow(progress, 1.5);
  const theta = (2 * Math.PI * i) / GOLDEN_RATIO;
  const phi = Math.acos(1 - 2 * norm);
  const radius = IVIZ_CORE_R + IVIZ_CORE_OUTER_R * (1 - ease);
  const instability = Math.pow(1 - progress, 2);
  const wobbleX = Math.sin(time * 2 + norm * 100) * IVIZ_CORE_CHAOS * instability;
  const wobbleY = Math.cos(time * 1.5 + norm * 200) * IVIZ_CORE_CHAOS * instability;
  const wobbleZ = Math.sin(time * 3 - norm * 300) * IVIZ_CORE_CHAOS * instability;
  const sinPhi = Math.sin(phi);
  const x = radius * sinPhi * Math.cos(theta) + wobbleX;
  const y = radius * sinPhi * Math.sin(theta) + wobbleY;
  const z = radius * Math.cos(phi) + wobbleZ;
  return [x, y, z, progress] as const;
}

// Foresight sun — layered stellar model (fusion core / convection shell /
// photosphere / corona flares), adapted and simplified from the_sun.jsx to
// fit this component's particle budget. Zone membership is fixed per index
// (like radar/iviz-mark used to be) so color can stay static; only position animates.
function hashNoise(n: number) {
  return Math.abs(Math.sin(n) * 43758.5453) % 1;
}

function foresightSunSegments(count: number) {
  const core = Math.floor(count * 0.15);
  const mid = Math.floor(count * 0.35);
  const outer = Math.floor(count * 0.3);
  return { core, mid, outer, flare: count - core - mid - outer };
}

function foresightSunZone(i: number, count: number) {
  const { core, mid, outer } = foresightSunSegments(count);
  if (i < core) return 0;
  if (i < core + mid) return 1;
  if (i < core + mid + outer) return 2;
  return 3;
}

const FORESIGHT_SUN_SCALE = 1.35;
const FORESIGHT_SUN_SPEED = 1.5;

function foresightSunPosition(i: number, count: number, rawTime: number) {
  const time = rawTime * FORESIGHT_SUN_SPEED;
  const { core, mid, outer } = foresightSunSegments(count);
  const h1 = hashNoise(i * 12.9898);
  const h2 = hashNoise(i * 78.233);
  const h3 = hashNoise(i * 45.164);
  const theta = h1 * Math.PI * 2;
  const cphi = h2 * 2 - 1;
  const sphi = Math.sqrt(Math.max(0, 1 - cphi * cphi));

  if (i < core) {
    const r = 0.38 * Math.cbrt(Math.max(h3, 0.0001));
    const jitter = Math.sin(time * 3 + h3 * 6.283) * 0.025;
    const rad = r + jitter;
    return [rad * sphi * Math.cos(theta), rad * sphi * Math.sin(theta), rad * cphi] as const;
  }
  if (i < core + mid) {
    const rMin = 0.38;
    const rMax = 0.82;
    const rr = rMin + h1 * (rMax - rMin);
    const cell = Math.sin(theta * 6 + time * 0.6) + Math.sin(cphi * 18 + time * 0.5 + h3 * 6.283);
    const flow = cell * 0.04;
    const rad = rr + flow;
    return [rad * sphi * Math.cos(theta + flow * 0.05), rad * sphi * Math.sin(theta + flow * 0.05), rad * cphi] as const;
  }
  if (i < core + mid + outer) {
    const R = 1.02;
    const ripple = Math.sin(theta * 14 + time * 0.5) + Math.sin(cphi * 16 - time * 0.4 + h3 * 6.283);
    const rad = R + ripple * 0.04;
    return [rad * sphi * Math.cos(theta), rad * sphi * Math.sin(theta), rad * cphi] as const;
  }
  // Corona: uses the same full-sphere direction as every other zone (not a
  // small fixed set of directions), so it reads as a diffuse pulsing halo
  // instead of a handful of rigid spikes shooting out to the frame edge.
  const pulse = 0.6 + 0.4 * Math.sin(time * 0.5 + h3 * 6.283);
  const length = (0.15 + h2 * 0.22) * pulse;
  const rad = 1.05 + length;
  const wiggle = Math.sin(h1 * 6 + time * 1.2 + h3 * 6.283) * 0.03;
  return [rad * sphi * Math.cos(theta) + wiggle, rad * sphi * Math.sin(theta) + wiggle, rad * cphi] as const;
}

// AI brain — bilateral cortical-fold sphere, adapted from ai_brain.jsx.
// Rendered small (avatar scale) as Merlin/Mona's "living brain" indicator,
// so its own color isn't set here — it uses the component's normal
// accent/lightAccent mix, letting each assistant instance be recolored.
const BRAIN_SCALE = 0.3;
function aiBrainPosition(i: number, count: number, time: number, scaleMult = 1) {
  const size = 68;
  const fold = 1.15;
  const pulse = 1.2;
  const chaos = 0.8;
  const spin = 0.22;

  const u = (i + 0.5) / count;
  const golden = 2.399963229728653;
  const a = i * golden + time * spin;
  const z0 = 1 - 2 * u;
  const r0 = Math.sqrt(Math.max(0, 1 - z0 * z0));

  let x = Math.cos(a) * r0;
  let y = Math.sin(a) * r0;
  let z = z0;

  const hemi = x < 0 ? -1 : 1;
  const ax = Math.abs(x);

  const gx = Math.sin(y * 10.5 + time * 1.7 + hemi * 0.8);
  const gy = Math.sin(z * 13.0 - time * 1.35 + x * 2.4);
  const gz = Math.cos((x + y) * 11.0 + time * 1.1);
  const foldWave = Math.sin((gx + gy + gz) * 2.7 + ax * 15.0 * fold + z * 5.0);
  const pulseWave = 1.0 + 0.055 * Math.sin(time * 4.0 - a * 0.7 + z * 12.0) * pulse;

  x *= 1.28;
  y *= 0.98;
  z *= 0.92;

  x += Math.sin(y * 7.0 + z * 4.0 + time * 0.8) * 2.8 * fold;
  y += Math.cos(z * 8.0 - x * 3.0 - time) * 2.2 * fold;
  z += Math.sin(x * 6.0 + y * 5.0 + time * 0.7) * 2.6 * fold;

  const groove = Math.sin(Math.atan2(y, z) * 10.0 + ax * 18.0 * fold + foldWave * 1.8);
  const cortex = 1.0 + 0.075 * groove + 0.035 * foldWave;
  const gap = 3.8 * Math.exp(-Math.abs(x) * 7.0);

  x = x * cortex * (size / 100.0);
  y = y * cortex * (size / 100.0);
  z = z * cortex * (size / 100.0);

  x *= 1.0 + 0.1 * Math.sin(z * 0.09 + time * 0.8);
  y *= pulseWave;
  z *= 1.0 + 0.06 * Math.cos(y * 0.08 - time);
  x += hemi * gap;

  const aiPhase = Math.sin(a * 0.55 + time * (1.4 + pulse * 0.25) + z * 0.11);
  const aiRadius = 1.0 + chaos * 0.12 * aiPhase;
  x += Math.sin(y * 0.16 + time * 1.4) * chaos * 2.2 * aiRadius;
  y += Math.cos(z * 0.14 - time * 1.1) * chaos * 1.8 * aiRadius;
  z += Math.sin(x * 0.12 + time * 1.25) * chaos * 2.0 * aiRadius;

  const s = BRAIN_SCALE * scaleMult;
  return [x * s, y * s, z * s] as const;
}

// Coharts mark — sampled from the real logo icon (see cohartsMarkPoints.ts)
// instead of an invented shape, each point keeping the logo's actual pixel
// color (blue-to-purple gradient). count is usually larger than the sampled
// point set, so indices past LOGO_POINT_COUNT wrap around and get extra
// jitter, filling the mark out into a fuller particle cloud without
// duplicating any point exactly.
const LOGO_POINT_COUNT = LOGO_POINTS.length / 5;
const LOGO_JITTER = 0.045;
const LOGO_Z_DEPTH = 0.16;

function cohartsMarkColor(base: number) {
  return [LOGO_POINTS[base * 5 + 2], LOGO_POINTS[base * 5 + 3], LOGO_POINTS[base * 5 + 4]] as const;
}

function cohartsMarkPosition(i: number, count: number, time: number) {
  const base = i % LOGO_POINT_COUNT;
  const repeat = Math.floor(i / LOGO_POINT_COUNT);
  const bx = LOGO_POINTS[base * 5];
  const by = LOGO_POINTS[base * 5 + 1];

  const seed = base * 12.9898 + repeat * 78.233;
  const jx = repeat === 0 ? 0 : (hashNoise(seed) - 0.5) * LOGO_JITTER;
  const jy = repeat === 0 ? 0 : (hashNoise(seed + 33.7) - 0.5) * LOGO_JITTER;
  const jz = (hashNoise(seed + 91.1) - 0.5) * LOGO_Z_DEPTH;

  const wobble = Math.sin(time * 0.6 + base * 0.05) * 0.012;
  const breathe = 1 + Math.sin(time * 0.5) * 0.02;

  const x = (bx + jx + wobble) * breathe;
  const y = (by + jy) * breathe;
  const z = jz + Math.sin(time * 0.4 + base * 0.08) * 0.03;
  return [x, y, z] as const;
}

// Nearest-neighbor pairs baked from the real point cloud (see
// cohartsMarkPoints.ts), so wires trace the mark's actual contour locally
// instead of crossing the whole shape. Reused directly as particle-index
// pairs since count is always >= LOGO_POINT_COUNT, so indices
// 0..LOGO_POINT_COUNT-1 map 1:1 to the sampled points with no wrap/jitter
// (repeat === 0 above).
function cohartsMarkWirePairs(): Array<[number, number]> {
  return LOGO_WIRE_PAIRS.map(([a, b]) => [a, b]);
}

// Scroll-driven divergence: particles fly outward and toward the camera as
// the section scrolls past, so the formation visibly falls apart rather
// than just translating — this is the motion scroll progress drives.
function applyScatter(x: number, y: number, z: number, scatter: number): [number, number, number] {
  const scaleOut = 1 + scatter * 2.4;
  return [x * scaleOut, y * scaleOut, z * scaleOut + scatter * 2.2];
}

export function ParticleOrb({
  variant,
  accent = "#4A90D9",
  lightAccent,
  offsetX = 0,
  offsetY = 0,
  scrollProgress,
}: {
  variant: Variant;
  accent?: string;
  lightAccent?: string;
  offsetX?: number;
  offsetY?: number;
  scrollProgress?: MotionValue<number>;
}) {
  const mountRef = useRef<HTMLDivElement>(null);
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.innerWidth < 768;
    let disposed = false;
    let cleanupInner: (() => void) | undefined;

    import("three").then((THREE) => {
      if (disposed || !mount) return;

      const count = variant === "ai-brain" ? 450 : isMobile ? 900 : 2200;
      const [r0, g0, b0] = hexToRgb01(isLight ? lightAccent ?? accent : accent);
      // coharts-mark sits directly behind the hero's centered text as a
      // watermark-like backdrop, so it's dimmed well below the other
      // full-strength hero variants to keep the overlapping text legible.
      const baseOpacity = variant === "coharts-mark" ? (isLight ? 0.55 : 0.4) : isLight ? 0.95 : 0.85;

      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(50, mount.clientWidth / mount.clientHeight, 0.1, 100);
      camera.position.z = variant === "ai-brain" ? 5.4 : 4.8;

      // coharts-mark's wire mesh follows the sampled logo contour, so
      // connections stay local instead of crossing the whole shape.
      const wirePairs = variant === "coharts-mark" ? cohartsMarkWirePairs() : [];
      const WIRE_COUNT = wirePairs.length;

      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: !isMobile });
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, isMobile ? 1.5 : 2));
      renderer.setSize(mount.clientWidth, mount.clientHeight);
      mount.appendChild(renderer.domElement);

      const positions = new Float32Array(count * 3);
      const colors = new Float32Array(count * 3);
      const geometry = new THREE.BufferGeometry();

      const [navyR, navyG, navyB] = hexToRgb01(isLight ? "#1B2A3F" : "#8CA3C7");
      const [goldR, goldG, goldB] = hexToRgb01("#C9A55E");
      const [ivizAccentR, ivizAccentG, ivizAccentB] = hexToRgb01("#4A90D9");

      for (let i = 0; i < count; i++) {
        if (variant === "foresight-sun") {
          const zone = foresightSunZone(i, count);
          if (zone === 0) {
            colors[i * 3] = lerp(goldR, 1, 0.7);
            colors[i * 3 + 1] = lerp(goldG, 1, 0.7);
            colors[i * 3 + 2] = lerp(goldB, 1, 0.7);
          } else if (zone === 1) {
            colors[i * 3] = goldR;
            colors[i * 3 + 1] = goldG;
            colors[i * 3 + 2] = goldB;
          } else if (zone === 2) {
            colors[i * 3] = r0;
            colors[i * 3 + 1] = g0;
            colors[i * 3 + 2] = b0;
          } else {
            const boost = 0.3 + Math.random() * 0.3;
            colors[i * 3] = lerp(goldR, 1, boost);
            colors[i * 3 + 1] = lerp(goldG, 1, boost);
            colors[i * 3 + 2] = lerp(goldB, 1, boost);
          }
        } else if (variant === "ai-brain") {
          const mix = Math.random();
          const floor = isLight ? 0.68 : 0.6;
          colors[i * 3] = r0 * (floor + mix * (1 - floor));
          colors[i * 3 + 1] = g0 * (floor + mix * (1 - floor));
          colors[i * 3 + 2] = b0 * (floor + mix * (1 - floor));
        } else if (variant === "coharts-mark") {
          const [lr, lg, lb] = cohartsMarkColor(i % LOGO_POINT_COUNT);
          colors[i * 3] = lr;
          colors[i * 3 + 1] = lg;
          colors[i * 3 + 2] = lb;
        } else {
          // iviz-core: recolored live each frame in writePositions (progress-based);
          // seed with navy so the very first paint before frame 1 isn't blank.
          colors[i * 3] = navyR;
          colors[i * 3 + 1] = navyG;
          colors[i * 3 + 2] = navyB;
        }
      }
      geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));

      const material = new THREE.PointsMaterial({
        size: (isMobile ? 0.028 : 0.022) * (isLight ? 1.15 : 1),
        vertexColors: true,
        transparent: true,
        opacity: baseOpacity,
        depthWrite: false,
        blending: variant === "ai-brain" && !isLight ? THREE.AdditiveBlending : THREE.NormalBlending,
      });
      const points = new THREE.Points(geometry, material);
      points.position.x = offsetX;
      points.position.y = offsetY;
      scene.add(points);

      // Full-bleed hero variants: their world-space size is fixed, but a
      // PerspectiveCamera's horizontal FOV grows with the container's aspect
      // ratio, so on a wide screen the same formation covers a shrinking
      // fraction of the frame. Scale the formation up with aspect ratio so
      // it keeps filling the section on any width. coharts-mark is excluded —
      // it's a contained watermark behind the centered hero text, not a
      // full-bleed background, so it keeps its natural size on wide screens.
      const isHeroVariant = variant === "iviz-core" || variant === "foresight-sun";
      const BASELINE_ASPECT = 1.5;
      const COHARTS_MARK_SCALE = 1.6;
      function computeFillScale() {
        if (variant === "coharts-mark") return COHARTS_MARK_SCALE;
        if (!isHeroVariant) return 1;
        return Math.max(1, mount.clientWidth / mount.clientHeight / BASELINE_ASPECT);
      }
      points.scale.setScalar(computeFillScale());

      const wireGeometry = new THREE.BufferGeometry();
      let wireLines: import("three").LineSegments | null = null;
      if (WIRE_COUNT > 0) {
        wireGeometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(WIRE_COUNT * 6), 3));
        const wireMaterial = new THREE.LineBasicMaterial({
          color: new THREE.Color(r0, g0, b0),
          transparent: true,
          opacity: isLight ? 0.3 : 0.22,
        });
        wireLines = new THREE.LineSegments(wireGeometry, wireMaterial);
        wireLines.position.x = offsetX;
        wireLines.position.y = offsetY;
        wireLines.scale.setScalar(computeFillScale());
        scene.add(wireLines);
      }

      function writePositions(time: number, scatter: number) {
        const pos = geometry.attributes.position.array as Float32Array;
        const col = geometry.attributes.color.array as Float32Array;
        for (let i = 0; i < count; i++) {
          let x: number, y: number, z: number;
          if (variant === "iviz-core") {
            const [px, py, pz, progress] = ivizCorePosition(i, count, time);
            x = px;
            y = py;
            z = pz;
            let mr = lerp(navyR, ivizAccentR, progress);
            let mg = lerp(navyG, ivizAccentG, progress);
            let mb = lerp(navyB, ivizAccentB, progress);
            if (progress > 0.9) {
              const burst = (progress - 0.9) / 0.1;
              mr = lerp(mr, 1, burst * 0.5);
              mg = lerp(mg, 1, burst * 0.5);
              mb = lerp(mb, 1, burst * 0.5);
            }
            col[i * 3] = mr;
            col[i * 3 + 1] = mg;
            col[i * 3 + 2] = mb;
          } else if (variant === "ai-brain") {
            [x, y, z] = aiBrainPosition(i, count, time);
          } else if (variant === "coharts-mark") {
            [x, y, z] = cohartsMarkPosition(i, count, time);
          } else {
            [x, y, z] = foresightSunPosition(i, count, time);
            x *= FORESIGHT_SUN_SCALE;
            y *= FORESIGHT_SUN_SCALE;
            z *= FORESIGHT_SUN_SCALE;
          }
          if (scatter > 0) [x, y, z] = applyScatter(x, y, z, scatter);
          pos[i * 3] = x;
          pos[i * 3 + 1] = y;
          pos[i * 3 + 2] = z;
        }
        geometry.attributes.position.needsUpdate = true;
        if (variant === "iviz-core") geometry.attributes.color.needsUpdate = true;

        if (wireLines) {
          const wpos = wireGeometry.attributes.position.array as Float32Array;
          for (let k = 0; k < wirePairs.length; k++) {
            const [ia, ib] = wirePairs[k];
            wpos[k * 6] = pos[ia * 3];
            wpos[k * 6 + 1] = pos[ia * 3 + 1];
            wpos[k * 6 + 2] = pos[ia * 3 + 2];
            wpos[k * 6 + 3] = pos[ib * 3];
            wpos[k * 6 + 4] = pos[ib * 3 + 1];
            wpos[k * 6 + 5] = pos[ib * 3 + 2];
          }
          wireGeometry.attributes.position.needsUpdate = true;
        }
      }

      writePositions(0, 0);
      renderer.render(scene, camera);

      let raf = 0;
      let running = true;
      const start = performance.now();

      function frame(now: number) {
        if (!running) return;
        const time = (now - start) / 1000;
        const scatter = reducedMotion ? 0 : Math.max(0, scrollProgress?.get() ?? 0);
        if (!reducedMotion) {
          writePositions(time, scatter);
          // coharts-mark is a near-flat sampled silhouette, not a 3D volume —
          // a full spin would foreshorten it into a sliver, so it sways
          // gently instead, always facing the camera.
          const rot = variant === "coharts-mark" ? Math.sin(time * 0.15) * 0.12 : time * 0.03;
          points.rotation.y = rot;
          material.opacity = baseOpacity * Math.max(0, 1 - scatter * 1.8);
          if (wireLines) {
            wireLines.rotation.y = rot;
            (wireLines.material as import("three").LineBasicMaterial).opacity =
              (isLight ? 0.3 : 0.22) * Math.max(0, 1 - scatter * 1.8);
          }
        }
        renderer.render(scene, camera);
        if (!reducedMotion) raf = requestAnimationFrame(frame);
      }
      if (!reducedMotion) raf = requestAnimationFrame(frame);

      function resize() {
        if (!mount) return;
        camera.aspect = mount.clientWidth / mount.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(mount.clientWidth, mount.clientHeight);
        const fillScale = computeFillScale();
        points.scale.setScalar(fillScale);
        if (wireLines) wireLines.scale.setScalar(fillScale);
        renderer.render(scene, camera);
      }
      window.addEventListener("resize", resize);

      const observer = new IntersectionObserver(
        ([entry]) => {
          running = entry.isIntersecting;
          if (running && !reducedMotion) raf = requestAnimationFrame(frame);
          else cancelAnimationFrame(raf);
        },
        { threshold: 0 }
      );
      observer.observe(mount);

      cleanupInner = () => {
        cancelAnimationFrame(raf);
        window.removeEventListener("resize", resize);
        observer.disconnect();
        geometry.dispose();
        material.dispose();
        if (wireLines) {
          wireGeometry.dispose();
          (wireLines.material as import("three").LineBasicMaterial).dispose();
        }
        renderer.dispose();
        mount.removeChild(renderer.domElement);
      };
    });

    return () => {
      disposed = true;
      cleanupInner?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant, accent, lightAccent, isLight, offsetX, offsetY]);

  return <div ref={mountRef} className="w-full h-full" aria-hidden="true" />;
}
